import { useState } from 'react';
import axios from 'axios';

import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { useAuth } from '../../../../context/AuthContext';

const AddPaymentModal = ({ isOpen, onClose, order, balance, onSuccess }) => {
  const [form, setForm] = useState({
    amount: '',
    paymentMode: 'CASH',
    transactionId: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  if (!isOpen) return null;

  const amt = Number(form.amount) || 0;
  const remaining = balance - amt;

  const handleSubmit = async () => {
    if (amt <= 0) return alert("Please enter a valid amount.");
    if (amt > balance) return alert(`Amount cannot exceed balance of ₹${balance.toLocaleString('en-IN')}`);

    try {
      setLoading(true);

      const payload = {
        amount: amt,
        paymentMode: form.paymentMode,
        transactionId: form.transactionId?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
        ...(user?.role !== "superadmin" && user?.assignedDiagnosticCenters?.[0]?.diagnosticCenterId
          ? { diagnosticCenterId: user.assignedDiagnosticCenters[0].diagnosticCenterId }
          : {}),
      };

      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/payments/orders/${order.id}/payments`,
        payload,
        { withCredentials: true }
      );

      onSuccess?.();
      onClose?.();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  const fillFullBalance = () => {
    setForm((prev) => ({ ...prev, amount: String(balance) }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">

        {/* Header */}
        <div className="bg-green-600 text-white p-5 text-center rounded-t-xl">
          <Icon name="CreditCard" size={32} className="mx-auto mb-2" />
          <h2 className="text-xl font-semibold">Record Payment</h2>
          <p className="text-sm opacity-90 mt-1">Order #{order.id}</p>
        </div>

        <div className="p-5 space-y-4">

          {/* Balance Info */}
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="text-xs text-amber-800 font-medium">Balance Due</span>
            <span className="text-sm font-bold text-amber-900">₹{balance.toLocaleString('en-IN')}</span>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Amount (₹)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                max={balance}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder={`Enter amount (max ₹${balance.toLocaleString('en-IN')})`}
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
              />
              <button
                type="button"
                onClick={fillFullBalance}
                className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-700 transition-colors whitespace-nowrap"
              >
                Full Amount
              </button>
            </div>
            {amt > 0 && amt < balance && (
              <p className="text-[10px] text-blue-600 mt-1 flex items-center gap-1">
                <Icon name="Info" size={10} />
                Partial payment — ₹{remaining.toLocaleString('en-IN')} will remain as balance
              </p>
            )}
            {amt > 0 && amt >= balance && (
              <p className="text-[10px] text-green-600 mt-1 flex items-center gap-1">
                <Icon name="CheckCircle" size={10} />
                Full payment — balance will be cleared
              </p>
            )}
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={form.paymentMode}
              onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            >
              <option value="CASH">Cash</option>
              <option value="CARD">Card</option>
              <option value="UPI">UPI</option>
              <option value="NET_BANKING">Net Banking</option>
              <option value="WALLET">Wallet</option>
              <option value="CHEQUE">Cheque</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
            </select>
          </div>

          {/* Transaction ID */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Transaction ID (Optional)
            </label>
            <input
              type="text"
              value={form.transactionId}
              onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="Enter transaction/reference ID"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="e.g., Split payment - UPI portion"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 text-sm py-2"
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={loading || amt <= 0 || amt > balance}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm py-2"
            >
              {loading ? 'Saving...' : amt > 0 && amt < balance ? `Pay ₹${amt.toLocaleString('en-IN')}` : 'Record Payment'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;
